import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const STATES = ["Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan","Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal"];

export default function Settings() {
  const [form, setForm] = useState(null);
  const [modes, setModes] = useState({});
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const { razorpay_mode, shiprocket_mode, rate_source, rate_poll_interval_minutes, rate_stale_hours, purity_multipliers, ...rest } = r.data;
      setForm(rest);
      setModes({ razorpay_mode, shiprocket_mode });
    }).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!form) return <p className="text-neutral-400 font-mono text-sm">LOADING…</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (section, k, v) => setForm((f) => ({ ...f, [section]: { ...(f[section] || {}), [k]: v } }));

  const uploadBrand = (slot) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Keep the file under 500KB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/admin/settings/brand-asset", { slot, data_url: reader.result });
        toast.success("Brand asset updated");
      } catch (err) { toast.error(errMsg(err)); }
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const changePassword = async () => {
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setRate = (i, k, v) =>
    set("shipping_rates", form.shipping_rates.map((r, j) => (j === i ? { ...r, [k]: k === "price" ? Number(v) : v } : r)));

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-settings-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Settings</h1>

      <section className="bg-white border border-neutral-200 p-6 space-y-4">
        <h2 className="font-heading font-semibold">Store</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Store name</Label><Input value={form.store_name} onChange={(e) => set("store_name", e.target.value)} data-testid="set-store-name" /></div>
          <div><Label>Logo URL</Label><Input value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} data-testid="set-logo-url" /></div>
          <div><Label>Currency</Label><Input value={form.currency} disabled data-testid="set-currency" /></div>
          <div><Label>Jewellery GST (%)</Label><Input type="number" step="0.1" value={form.gst_rate} onChange={(e) => set("gst_rate", Number(e.target.value))} data-testid="set-gst-rate" /></div>
          <div><Label>GSTIN</Label><Input value={form.gst_number} onChange={(e) => set("gst_number", e.target.value)} data-testid="set-gst-number" /></div>
          <div>
            <Label>Store state (CGST/SGST vs IGST)</Label>
            <select value={form.store_state} onChange={(e) => set("store_state", e.target.value)} className="w-full h-10 border border-neutral-200 rounded-md px-3 text-sm bg-white" data-testid="set-store-state">
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>Support email</Label><Input value={form.support_email} onChange={(e) => set("support_email", e.target.value)} data-testid="set-support-email" /></div>
          <div className="sm:col-span-2"><Label>Announcement bar</Label><Input value={form.announcement} onChange={(e) => set("announcement", e.target.value)} data-testid="set-announcement" /></div>
        </div>
      </section>

      <section className="bg-white border border-neutral-200 p-6 space-y-4">
        <h2 className="font-heading font-semibold">Pricing & Gifting</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label>Cart price lock (minutes)</Label><Input type="number" value={form.price_lock_minutes} onChange={(e) => set("price_lock_minutes", Number(e.target.value))} data-testid="set-price-lock" /></div>
          <div><Label>Gift wrap fee (₹)</Label><Input type="number" value={form.gift_wrap_fee} onChange={(e) => set("gift_wrap_fee", Number(e.target.value))} data-testid="set-gift-fee" /></div>
          <div><Label>Free shipping above (₹, 0 = off)</Label><Input type="number" value={form.free_shipping_threshold} onChange={(e) => set("free_shipping_threshold", Number(e.target.value))} data-testid="set-free-shipping" /></div>
        </div>
      </section>

      <section className="bg-white border border-neutral-200 p-6 space-y-3">
        <h2 className="font-heading font-semibold">Live rate strip placement</h2>
        <p className="text-xs text-neutral-500">Where the live metal rate appears on the storefront. Header stays off — the rate lives where it is contextually useful. Changes apply without a rebuild.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[["header", "Sticky header (not recommended)"], ["footer", "Footer — quiet single line"],
            ["coins_home", "Coins landing — feature block"], ["gold_home", "Gold landing — band below hero"],
            ["product_breakup", "Product pages — inside price breakup"]].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <Switch
                checked={(form.rate_strip_locations || ["footer", "coins_home", "gold_home", "product_breakup"]).includes(key)}
                onCheckedChange={(v) => set("rate_strip_locations", v
                  ? [...(form.rate_strip_locations || ["footer", "coins_home", "gold_home", "product_breakup"]), key]
                  : (form.rate_strip_locations || ["footer", "coins_home", "gold_home", "product_breakup"]).filter((x) => x !== key))}
                data-testid={`set-rate-loc-${key}`} />
              {label}
            </label>
          ))}
        </div>
      </section>

      <section className="bg-white border border-neutral-200 p-6 space-y-4">
        <h2 className="font-heading font-semibold">Shipping & COD</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <div><Label>COD fee (₹)</Label><Input type="number" value={form.cod_fee} onChange={(e) => set("cod_fee", Number(e.target.value))} data-testid="set-cod-fee" /></div>
          <label className="flex items-center gap-2 text-sm pb-2">
            <Switch checked={form.cod_enabled} onCheckedChange={(v) => set("cod_enabled", v)} data-testid="set-cod-toggle" />
            Cash on Delivery enabled
          </label>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Shipping rates</Label>
            <Button size="sm" variant="outline" onClick={() => set("shipping_rates", [...form.shipping_rates, { id: `rate-${Date.now()}`, name: "", price: 0, eta: "" }])} data-testid="add-shipping-rate">
              <Plus size={13} className="mr-1" /> Add Rate
            </Button>
          </div>
          {form.shipping_rates.map((r, i) => (
            <div key={r.id} className="grid grid-cols-4 gap-2 mb-2 items-center" data-testid={`shipping-rate-${i}`}>
              <Input value={r.name} onChange={(e) => setRate(i, "name", e.target.value)} placeholder="Name" className="col-span-2" data-testid={`rate-name-${i}`} />
              <Input type="number" value={r.price} onChange={(e) => setRate(i, "price", e.target.value)} placeholder="₹" data-testid={`rate-price-${i}`} />
              <div className="flex gap-1">
                <Input value={r.eta} onChange={(e) => setRate(i, "eta", e.target.value)} placeholder="ETA" data-testid={`rate-eta-${i}`} />
                <Button variant="ghost" size="icon" onClick={() => set("shipping_rates", form.shipping_rates.filter((_, j) => j !== i))}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-neutral-200 p-6">
        <h2 className="font-heading font-semibold mb-2">Integrations</h2>
        <p className="text-sm text-neutral-600">
          Razorpay: <span className="font-mono font-semibold uppercase" data-testid="razorpay-mode">{modes.razorpay_mode}</span>
          {" · "}Shiprocket: <span className="font-mono font-semibold uppercase" data-testid="shiprocket-mode">{modes.shiprocket_mode}</span>
        </p>
        <p className="text-xs text-neutral-500 mt-2">
          Keys live in <code className="font-mono">/app/backend/.env</code> — see <code className="font-mono">.env.example</code>. Metal rate source is managed under <a href="/admin/rates" className="underline">Metal Rates</a>.
        </p>
      </section>

      <section className="bg-white border border-neutral-200 p-6 space-y-4">
        <h2 className="font-heading font-semibold">Brand & Legal</h2>
        <p className="text-xs text-neutral-500">Shown on the footer, invoices and policy pages. Empty fields render nothing anywhere — no labels, no dashes. Blank BIS registration also suppresses every hallmark badge on gold.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Legal entity name</Label><Input value={form.legal?.legal_name || ""} onChange={(e) => setNested("legal", "legal_name", e.target.value)} data-testid="set-legal-name" /></div>
          <div><Label>CIN</Label><Input value={form.legal?.cin || ""} onChange={(e) => setNested("legal", "cin", e.target.value)} data-testid="set-cin" /></div>
          <div><Label>BIS hallmark registration</Label><Input value={form.legal?.bis_reg || ""} onChange={(e) => setNested("legal", "bis_reg", e.target.value)} placeholder="HM/C-…" data-testid="set-bis" /></div>
          <div className="sm:col-span-2"><Label>Address (invoices, footer, policies — single address used everywhere)</Label><Input value={form.legal?.address || ""} onChange={(e) => setNested("legal", "address", e.target.value)} data-testid="set-reg-address" /></div>
          <div><Label>Grievance officer (name · email · phone)</Label><Input value={form.legal?.grievance_officer || ""} onChange={(e) => setNested("legal", "grievance_officer", e.target.value)} data-testid="set-grievance" /></div>
          <div><Label>Manufacturer name</Label><Input value={form.legal?.manufacturer_name || ""} onChange={(e) => setNested("legal", "manufacturer_name", e.target.value)} data-testid="set-manufacturer" /></div>
          <div><Label>Consumer care — name</Label><Input value={form.legal?.consumer_care_name || ""} onChange={(e) => setNested("legal", "consumer_care_name", e.target.value)} data-testid="set-care-name" /></div>
          <div><Label>Consumer care — phone</Label><Input value={form.legal?.consumer_care_phone || ""} onChange={(e) => setNested("legal", "consumer_care_phone", e.target.value)} data-testid="set-care-phone" /></div>
          <div><Label>Consumer care — email</Label><Input value={form.legal?.consumer_care_email || ""} onChange={(e) => setNested("legal", "consumer_care_email", e.target.value)} data-testid="set-care-email" /></div>
          <div><Label>Country of origin</Label><Input value={form.legal?.country_of_origin || "India"} onChange={(e) => setNested("legal", "country_of_origin", e.target.value)} data-testid="set-origin" /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-neutral-100 pt-4">
          <div><Label>Support email</Label><Input value={form.support?.email || ""} onChange={(e) => setNested("support", "email", e.target.value)} data-testid="set-sup-email" /></div>
          <div><Label>Support phone</Label><Input value={form.support?.phone || ""} onChange={(e) => setNested("support", "phone", e.target.value)} data-testid="set-sup-phone" /></div>
          <div><Label>WhatsApp number</Label><Input value={form.support?.whatsapp || ""} onChange={(e) => setNested("support", "whatsapp", e.target.value)} placeholder="91xxxxxxxxxx" data-testid="set-sup-whatsapp" /></div>
          <div><Label>Support hours</Label><Input value={form.support?.hours || ""} onChange={(e) => setNested("support", "hours", e.target.value)} placeholder="Mon–Sat, 10am–7pm" data-testid="set-sup-hours" /></div>
        </div>
        <div className="border-t border-neutral-100 pt-4">
          <Label>Logo & favicon</Label>
          <div className="grid grid-cols-3 gap-3 mt-2">
            {[["logo_light", "Colour (light bg)"], ["logo_dark", "White (dark bg)"], ["favicon", "Favicon"]].map(([slot, label]) => (
              <label key={slot} className="border border-dashed border-neutral-300 rounded-lg p-3 text-center cursor-pointer hover:border-brand transition-colors" data-testid={`brand-upload-${slot}`}>
                <span className="text-xs text-neutral-500 block">{label}</span>
                {form.brand?.[slot] && <img src={form.brand[slot]} alt="" className="h-8 mx-auto mt-2 object-contain" />}
                <input type="file" accept="image/*" className="hidden" onChange={uploadBrand(slot)} />
              </label>
            ))}
          </div>
        </div>
      </section>

      <Button onClick={save} className="bg-stone-900 hover:bg-brand text-white btn-press" data-testid="save-settings-btn">Save Settings</Button>

      <section className="bg-white border border-neutral-200 p-6 space-y-4">
        <h2 className="font-heading font-semibold">Change Password</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" /></div>
          <div><Label>New password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" /></div>
        </div>
        <Button variant="outline" onClick={changePassword} data-testid="change-password-btn">Update Password</Button>
      </section>
    </div>
  );
}
